import React, {
  CSSProperties, FC, useState, useEffect, useRef,
} from 'react';
import { NEXT_STATIC_IMAGES } from 'src/common/constants';
import getClassNames from 'src/common/utils/getClassNames';
import classNames from 'classnames';
import { buildImageSrc, buildSrcSet } from 'src/common/utils/imageHelpers';
import styles from './Sticker.module.scss';
import stickers, { StickerCategories, StickerConfig } from './stickerMap';

export type StickerProps = {
  sticker: StickerConfig;
  alt?: string;
  withShadow?: boolean;
  type?: 'inline' | 'block';
  loading?: 'lazy' | 'eager';
  /** A specific height that should be used for the <img>. e.g. used in StickersHeader */
  height?: number;
  isInteractive?: boolean;
};

const FADE_OUT_DURATION = 200;
const FADE_IN_DURATION = 400;

export const getRandomSticker = (
  category: StickerCategories,
): StickerConfig => {
  const stickersOfCategory = stickers.filter(
    (sticker) => sticker.category === category,
  );
  return stickersOfCategory[Math.floor(Math.random() * stickersOfCategory.length)];
};

const Sticker: FC<StickerProps> = ({
  sticker, alt = '', withShadow, type, loading, height, isInteractive,
}) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [currentSticker, setCurrentSticker] = useState(sticker);
  const [transitionState, setTransitionState] = useState<'fadeIn' | 'fadeOut' | null>(null);
  const imgHeight = height || sticker?.height;

  const src = `${NEXT_STATIC_IMAGES}stickers/${currentSticker.name}.png`;
  const imgSrc = buildImageSrc(src, { h: imgHeight });
  const imgSrcSet = buildSrcSet(src, { h: imgHeight });

  const handleClick = () => {
    const userPrefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (isInteractive && !userPrefersReducedMotion && currentSticker.category) {
      let nextSticker = getRandomSticker(currentSticker.category);

      while (nextSticker.name === currentSticker.name) {
        nextSticker = getRandomSticker(currentSticker.category);
      }

      setTransitionState('fadeOut');
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setCurrentSticker(nextSticker);
      }, FADE_OUT_DURATION);
    }
  };

  const handleLoad = () => {
    if (isInteractive && transitionState === 'fadeOut') {
      setTransitionState('fadeIn');

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setTransitionState(null);
      }, FADE_IN_DURATION);
    }
  };

  // Clear timeout on unmount
  useEffect(() => () => {
    const timeoutId = timeoutRef.current;
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }, []);

  return (
    /* eslint-disable @next/next/no-img-element, jsx-a11y/no-noninteractive-element-interactions,
    jsx-a11y/click-events-have-key-events */
    <img
      alt={alt}
      className={getClassNames(styles, 'sticker', classNames({
        withShadow,
        doWiggle: isInteractive && transitionState === null,
        fadeOut: transitionState === 'fadeOut',
        fadeIn: transitionState === 'fadeIn',
        inline: type === 'inline',
        reducedMarginsLarge: currentSticker?.reducedMarginsLarge,
        reducedMarginsMedium: currentSticker?.reducedMarginsMedium,
      }))}
      style={{
        '--aspect-ratio': `${currentSticker?.width} / ${currentSticker?.height}`,
      } as CSSProperties}
      width={currentSticker?.width}
      height={currentSticker?.height}
      srcSet={imgSrcSet}
      src={imgSrc}
      loading={loading}
      draggable={false}
      onClick={handleClick}
      onLoad={handleLoad}
    />
    /* eslint-enable @next/next/no-img-element, jsx-a11y/no-noninteractive-element-interactions,
    jsx-a11y/click-events-have-key-events */
  );
};
export default Sticker;
